import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
  Typography, Chip, IconButton, CircularProgress, Divider, Alert, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  PublishedWithChanges as MakeLiveIcon,
  History as RestoreIcon,
  CheckCircle as LiveIcon,
} from '@mui/icons-material';
import config from '../config/config';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = config.API_BASE_URL;

// A version still being uploaded or transcoded will change state on its own, so the
// dialog polls while any row is in flight. Transcoding a long recording takes minutes;
// without this an admin uploads a replacement, sees PROCESSING, and has no idea whether
// to wait or reload.
const IN_FLIGHT = ['UPLOADING', 'PROCESSING'];
const POLL_MS = 5000;

// The nginx in front of the API refuses request bodies over 10 MB, so the file goes straight to
// S3 on a presigned URL, the way an asset's first upload does. Posting it through the API is
// only a fallback for a small file when S3 cannot be reached from the browser.
const DIRECT_FALLBACK_MAX_BYTES = 9 * 1024 * 1024;

const statusColor = (status) => {
  switch (status) {
    case 'READY': return 'success';
    case 'FAILED': return 'error';
    case 'PROCESSING': return 'info';
    case 'UPLOADING': return 'default';
    default: return 'default';
  }
};

const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const AssetVersionsDialog = ({ open, asset, onClose, onChanged, showNotification }) => {
  const { getAccessToken } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activatingVersion, setActivatingVersion] = useState(null);
  const pollRef = useRef(null);
  // Whether the error on screen came from loading the history. A refresh clears only that kind:
  // a failed upload's message must survive the refresh that follows it, and the 5s poll.
  const loadErrorRef = useRef(false);

  const assetId = asset?.id;

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error('Authentication token not available');
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const reportError = useCallback((err, fallback) => {
    if (err.response?.status === 401) return 'Session expired. Please login again.';
    if (err.response?.status === 403) return 'You do not have permission to do that.';
    return err.response?.data?.message || err.message || fallback;
  }, []);

  const fetchVersions = useCallback(async ({ quiet } = {}) => {
    if (!assetId) return;
    if (!quiet) setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/v1/content/assets/${assetId}/versions`,
        { headers: await authHeaders() },
      );
      setVersions(Array.isArray(res.data) ? res.data : []);
      if (loadErrorRef.current) {
        loadErrorRef.current = false;
        setError(null);
      }
    } catch (err) {
      loadErrorRef.current = true;
      setError(reportError(err, 'Could not load the version history.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [assetId, authHeaders, reportError]);

  useEffect(() => {
    if (open) {
      setFile(null);
      setNote('');
      setError(null);
      fetchVersions();
    }
  }, [open, fetchVersions]);

  // Poll only while something is in flight, and only while the dialog is open.
  useEffect(() => {
    const busy = versions.some((v) => IN_FLIGHT.includes(v.status));
    if (!open || !busy) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(() => fetchVersions({ quiet: true }), POLL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open, versions, fetchVersions]);

  // The browser's PUT to S3. Kept on its own so a failure can be told apart from an API error.
  const putToS3 = (uploadUrl, uploadContentType) => axios.put(uploadUrl, file, {
    headers: { 'Content-Type': uploadContentType },
    onUploadProgress: (e) => {
      if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
    },
  });

  // Small files only: through the API, which nginx caps at 10 MB.
  const postThroughApi = async () => {
    const form = new FormData();
    form.append('file', file);
    if (note.trim()) form.append('note', note.trim());
    await axios.post(
      `${API_BASE_URL}/api/v1/content/assets/${assetId}/versions`,
      form,
      {
        headers: { ...(await authHeaders()), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    const versionsUrl = `${API_BASE_URL}/api/v1/content/assets/${assetId}/versions`;
    try {
      // 1. Create the version and get a URL to put the file at.
      const { data: pending } = await axios.post(
        `${versionsUrl}/upload/initiate`,
        {
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          note: note.trim() || null,
        },
        { headers: await authHeaders() },
      );
      await fetchVersions({ quiet: true });

      // 2. Straight to S3. The Content-Type must be the one the URL was signed for.
      let viaApi = false;
      try {
        await putToS3(pending.uploadUrl, pending.uploadContentType);
      } catch (s3Error) {
        // Nothing reached S3: drop the version so it does not sit at "uploading" forever.
        await axios.post(`${versionsUrl}/${pending.versionNo}/upload/abort`, {},
          { headers: await authHeaders() }).catch(() => {});
        if (s3Error.response || file.size > DIRECT_FALLBACK_MAX_BYTES) {
          throw new Error(`The file could not be uploaded to storage${
            s3Error.response ? ` (${s3Error.response.status})` : ''}. Please try again.`);
        }
        console.warn('S3 upload failed, falling back to the API for a small file:', s3Error.message);
        setUploadProgress(0);
        await postThroughApi();
        viaApi = true;
      }

      // 3. Tell the backend it is there; it checks S3 and starts transcoding.
      if (!viaApi) {
        await axios.post(`${versionsUrl}/${pending.versionNo}/upload/complete`, {},
          { headers: await authHeaders() });
      }

      setFile(null);
      setNote('');
      showNotification?.('Replacement uploaded. It is not live until you make it live.', 'success');
      fetchVersions();
    } catch (err) {
      const msg = reportError(err, 'Upload failed.');
      loadErrorRef.current = false;
      setError(msg);
      showNotification?.(msg, 'error');
      fetchVersions({ quiet: true });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleActivate = async (version) => {
    const isRestore = !version.current && version.activatedAt;
    const what = isRestore
      ? `Restore version ${version.versionNo}?`
      : `Make version ${version.versionNo} live?`;
    if (!window.confirm(`${what}\n\nMembers will get this file from now on. Their listening position is kept where it still makes sense.`)) {
      return;
    }
    setActivatingVersion(version.versionNo);
    try {
      await axios.post(
        `${API_BASE_URL}/api/v1/content/assets/${assetId}/versions/${version.versionNo}/activate`,
        {},
        { headers: await authHeaders() },
      );
      showNotification?.(`Version ${version.versionNo} is now live.`, 'success');
      await fetchVersions();
      onChanged?.();
    } catch (err) {
      const msg = reportError(err, 'Could not make that version live.');
      loadErrorRef.current = false;
      setError(msg);
      showNotification?.(msg, 'error');
    } finally {
      setActivatingVersion(null);
    }
  };

  const busy = versions.some((v) => IN_FLIGHT.includes(v.status));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" component="span" fontWeight={700}>File versions</Typography>
          <Typography variant="body2" color="text.secondary">{asset?.title}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} title="Close"><CloseIcon /></IconButton>
      </DialogTitle>

      {busy && <LinearProgress />}

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Replace the file. Deliberately worded so nobody expects it to go live by itself. */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Replace file</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The new file is kept beside the live one. Members keep getting the current file until
            you make the new one live.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={uploading}>
              {file ? 'Change file' : 'Choose file'}
              <input
                type="file"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
              {file ? `${file.name} (${formatSize(file.size)})` : 'No file chosen'}
            </Typography>
          </Stack>
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 1.5 }}
            label="What changed? (optional)"
            placeholder="e.g. removed the dead air at the start"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={uploading}
          />
          <Button
            sx={{ mt: 1.5 }}
            variant="contained"
            onClick={handleUpload}
            disabled={!file || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? `Uploading… ${uploadProgress}%` : 'Upload replacement'}
          </Button>
          {uploading && (
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1.5, borderRadius: 1 }} />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>History</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : versions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No versions recorded yet. The original file predates version history; uploading a
            replacement starts it.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Length</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((v) => (
                <TableRow key={v.versionNo} sx={v.current ? { bgcolor: 'action.hover' } : undefined}>
                  <TableCell>{v.versionNo}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip size="small" label={v.status || '—'} color={statusColor(v.status)} />
                      {v.current && (
                        <Tooltip title={`Live since ${formatWhen(v.activatedAt)}${v.activatedBy ? ` — ${v.activatedBy}` : ''}`}>
                          <Chip size="small" color="primary" icon={<LiveIcon />} label="LIVE" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180, wordBreak: 'break-all' }}>{v.fileName || '—'}</TableCell>
                  <TableCell>{formatSize(v.sizeBytes)}</TableCell>
                  <TableCell>{formatDuration(v.durationSeconds)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">{formatWhen(v.createdAt)}</Typography>
                    {v.createdBy && <Typography variant="caption" color="text.secondary">{v.createdBy}</Typography>}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="caption">{v.note || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {v.current ? (
                      <Typography variant="caption" color="text.secondary">in use</Typography>
                    ) : v.status === 'READY' ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={activatingVersion === v.versionNo
                          ? <CircularProgress size={14} />
                          : (v.activatedAt ? <RestoreIcon /> : <MakeLiveIcon />)}
                        disabled={activatingVersion !== null}
                        onClick={() => handleActivate(v)}
                      >
                        {v.activatedAt ? 'Restore' : 'Make live'}
                      </Button>
                    ) : v.status === 'FAILED' ? (
                      <Tooltip title="This file could not be processed, so it cannot be made live. The live file is untouched.">
                        <Typography variant="caption" color="error">unusable</Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {v.status === 'PROCESSING' ? 'processing…' : 'uploading…'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetVersionsDialog;
